import React, { useState, useRef, useEffect, useMemo } from 'react';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import AutoReconciliationPage from './AutoReconciliationPage';
import ManualReconciliationPage from './ManualReconciliationPage';
import FileUploader from '../../shared/components/FileUploader';
import { API_BASE } from '../../shared/utils/apiConfig';
import { useAppData } from '../../core/contexts/AppDataContext';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { exportBankingRows, exportBankingPdf } from './bankingExportImport';
import localApiAdapter from '../../core/services/localApiAdapter';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import {
  Link as LinkIcon,
  CheckCircle,
  Clock,
  AlertTriangle,
  Building2,
  FileText,
  ChevronDown,
  ChevronUp,
  Filter,
  RefreshCcw,
  Shield,
  X,
  LayoutGrid,
  Layers,
  Eye,
} from 'lucide-react';

const ReconciliationPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.banque.pages.reconciliationPage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const formatMoney = (value) => formatBrandMoney(value);
  
  // ===== STATE MANAGEMENT =====
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showAutoReconciliationModal, setShowAutoReconciliationModal] = useState(false);
  const [showManualReconciliationModal, setShowManualReconciliationModal] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_reconciliations');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const { filters: columnFilters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  
  // Modals
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadAccount, setUploadAccount] = useState(null);
  const [uploadType, setUploadType] = useState('pdf');
  const [isUploading, setIsUploading] = useState(false);
  
  // Filters
  const [currentView, setCurrentView] = useState('all');
  const [columnVisibility, setColumnVisibility] = useState({
    account: true,
    bank: true,
    bankBalance: true,
    accountingBalance: true,
    reconciled: true,
    pending: true,
    discrepancies: true,
  });
  const [quickFilter, setQuickFilter] = useState('all');
  
  // Data
  const { bankingData, fetchBankAccounts } = useAppData();
  const accounts = bankingData?.accounts || [];
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  
  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  // ===== EFFECTS =====
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    const modalOpen = showViewModal || showAutoReconciliationModal || showManualReconciliationModal || showUploadModal;
    if (!modalOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showViewModal, showAutoReconciliationModal, showManualReconciliationModal, showUploadModal]);

  // ===== UPLOAD HANDLERS =====
  const handleFileUpload = async (files) => {
    if (!uploadAccount) {
      toast.error(p('toast_select_account'));
      return;
    }
    
    setIsUploading(true);
    
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('account_id', uploadAccount);
        formData.append('skip_duplicates', 'true');
        
        const endpoint = uploadType === 'pdf' 
          ? `${API_BASE}/banking/statements/upload-pdf`
          : `${API_BASE}/banking/statements/upload-excel`;
        
        const response = await fetch(endpoint, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || p('error_upload_failed'));
        }
        
        const result = await response.json();
        
        toast.success(
          p('toast_upload_success', {
            imported: result.data.imported,
            duplicates: result.data.duplicates,
            accuracy: result.data.validation?.accuracy || 100,
          })
        );
      }
      
      setShowUploadModal(false);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(p('toast_upload_error', { message: error.message }));
    } finally {
      setIsUploading(false);
    }
  };
  
  const openUploadModal = (accountId, type) => {
    setUploadAccount(accountId);
    setUploadType(type);
    setShowUploadModal(true);
  };

  // ===== HELPER FUNCTIONS =====
  const loadAccounts = async () => {
    setIsLoadingAccounts(true);
    try {
      await fetchBankAccounts();
    } catch (error) {
      console.error('Error loading accounts:', error);
      toast.error(p('toast_load_error'));
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  const reconciliationExportHeaders = [
    { key: 'name', label: p('col_account') },
    { key: 'bank', label: p('col_bank') },
    { key: 'bankBalance', label: p('col_bank_balance') },
    { key: 'accountingBalance', label: p('col_accounting_balance') },
    { key: 'reconciled', label: p('col_reconciled') },
    { key: 'pending', label: p('col_pending') },
    { key: 'discrepancies', label: p('col_discrepancies') },
    { key: 'status', label: p('col_status') },
  ];

  const handleExportCSV = () => { exportBankingRows(filteredAccounts, reconciliationExportHeaders, 'rapprochement', 'csv'); setShowExportDropdown(false); };
  const handleExportExcel = () => { exportBankingRows(filteredAccounts, reconciliationExportHeaders, 'rapprochement', 'excel'); setShowExportDropdown(false); };
  const handleExportPDF = () => { exportBankingPdf(p('title')); setShowExportDropdown(false); };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredRowIds);
    }
  };

  const handleItemSelect = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const handleView = (account) => {
    setSelectedAccount(account);
    setShowViewModal(true);
  };

  const reconciliationStatusOptions = useMemo(() => [
    { value: 'reconciled', label: p('badge_reconciled'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'pending', label: p('badge_pending'), icon: Clock, style: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
    { value: 'discrepancies', label: p('badge_discrepancies'), icon: AlertTriangle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
  ], [p]);

  const normalizeReconciliationStatus = (status) => {
    const normalized = String(status || 'pending').toLowerCase();
    if (normalized.includes('reconcil')) return 'reconciled';
    if (normalized.includes('discrep')) return 'discrepancies';
    return 'pending';
  };

  const getAccountReconStatus = (account) =>
    normalizeReconciliationStatus(account.reconciliationStatus || account.status);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.patch(`/banking/accounts/${recordId}/reconciliation-status`, { status: newStatus });
      await fetchBankAccounts();
      toast.success(p('update_success'));
    } catch (error) {
      console.error('Error updating reconciliation status:', error);
      toast.error(error.message || p('save_error'));
    }
  };

  const renderReconciliationStatusBadge = (account, { clickable = true } = {}) => (
    <QawaemClickableStatusBadge
      status={normalizeReconciliationStatus(account.reconciliationStatus || account.status)}
      recordId={account.id}
      options={reconciliationStatusOptions}
      onChange={clickable ? handleStatusChange : undefined}
      title={t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
    />
  );


  // Calculate stats
  const stats = useMemo(() => ({
    total: accounts.length,
    reconciled: accounts.filter((a) => getAccountReconStatus(a) === 'reconciled').length,
    pending: accounts.filter((a) => getAccountReconStatus(a) === 'pending').length,
    discrepancies: accounts.filter((a) => getAccountReconStatus(a) === 'discrepancies').length,
    operations: accounts.reduce((sum, a) => sum + (a.reconciled || 0) + (a.pending || 0), 0),
  }), [accounts]);

  const qawaemStats = useMemo(() => sliceQawaemStats([
    { key: 'total', label: p('stat_total_accounts'), value: stats.total, icon: Building2, variant: 'copper' },
    { key: 'reconciled', label: p('stat_reconciled'), value: stats.reconciled, icon: CheckCircle, variant: 'forest' },
    { key: 'pending', label: p('stat_pending'), value: stats.pending, icon: Clock, variant: 'copper' },
    { key: 'discrepancies', label: p('stat_discrepancies'), value: stats.discrepancies, icon: AlertTriangle, variant: 'forest' },
  ], 4), [stats, p]);

  const columnFilterRules = useMemo(() => [
    textColumnRule('account', (row) => row.name || row.account_name || ''),
    textColumnRule('bank', (row) => row.bank || row.bank_name || ''),
    textColumnRule('bankBalance', (row) => formatMoney(row.bankBalance ?? row.balance)),
    textColumnRule('accountingBalance', (row) => formatMoney(row.accountingBalance ?? row.balance)),
    textColumnRule('reconciled', (row) => String(row.reconciled ?? 0)),
    textColumnRule('pending', (row) => String(row.pending ?? 0)),
    textColumnRule('discrepancies', (row) => String(row.discrepancies ?? 0)),
  ], [formatMoney]);

  const baseFilteredAccounts = accounts.filter(account => {
    const reconStatus = getAccountReconStatus(account);
    const matchesView = currentView === 'all' || reconStatus === currentView;
    const matchesSearch = viewMode !== 'cards' || !searchTerm ||
      (account.account_name || account.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (account.bank_name || account.bank || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (account.account_number || account.accountNumber || '').toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesView || !matchesSearch) return false;

    if (activeDistributionFilter) {
      if (mainViewMode === 'by_bank') {
        const bankLabel = String(account.bank || account.bank_name || '').toLowerCase();
        if (!bankLabel.includes(String(activeDistributionFilter).toLowerCase())) return false;
      } else if (activeDistributionFilter !== 'total' && reconStatus !== activeDistributionFilter) {
        return false;
      }
    }

    return true;
  });

  const filteredAccounts = applyColumnFilters(baseFilteredAccounts, columnFilters, columnFilterRules);
  const filteredRowIds = useMemo(() => filteredAccounts.map((a) => a.id), [filteredAccounts]);
  const allRowsSelected = filteredRowIds.length > 0 && filteredRowIds.every((id) => selectedItems.includes(id));
  const someRowsSelected = selectedItems.length > 0 && !allRowsSelected;

  const qawaemColumnDefs = useMemo(() => [
    { key: 'account', label: p('col_account'), checked: columnVisibility.account !== false },
    { key: 'bank', label: p('col_bank'), checked: columnVisibility.bank !== false },
    { key: 'bankBalance', label: p('col_bank_balance'), checked: columnVisibility.bankBalance !== false },
    { key: 'accountingBalance', label: p('col_accounting_balance'), checked: columnVisibility.accountingBalance !== false },
    { key: 'reconciled', label: p('col_reconciled'), checked: columnVisibility.reconciled !== false },
    { key: 'pending', label: p('col_pending'), checked: columnVisibility.pending !== false },
    { key: 'discrepancies', label: p('col_discrepancies'), checked: columnVisibility.discrepancies !== false },
    { key: 'actions', label: p('col_actions'), checked: true, required: true },
  ], [columnVisibility, p]);
  const handleToggleColumn = (key) => {
    if (key === 'actions') return;
    const col = qawaemColumnDefs.find((c) => c.key === key);
    if (col?.required) return;
    setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => columnVisibility[key] !== false;
  const tableColSpan = 1 + ['account', 'bank', 'bankBalance', 'accountingBalance', 'reconciled', 'pending', 'discrepancies'].filter((k) => isColVisible(k)).length + 1;

  // ===== RENDER =====
  return (
    <QawaemErpListPageShell
      className="space-y-3"
      title={p('title')}
      subtitle={p('subtitle')}
      icon={LinkIcon}
      stats={qawaemStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      filterActions={selectedItems.length > 0 ? (
        <button
          type="button"
          onClick={() => setSelectedItems([])}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-white/20 bg-white/5 text-white/80 hover:bg-white/10 transition-colors text-sm font-medium shrink-0"
        >
          <X size={16} />
          <span>{t('common.clear_selection')} ({selectedItems.length})</span>
        </button>
      ) : null}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder', { defaultValue: 'بحث…' })}
      headerActions={(
        <>
          <button
            type="button"
            onClick={() => loadAccounts()}
            className="refresh-button px-3 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors"
            disabled={isLoadingAccounts}
            aria-label={t('common.refresh', { defaultValue: 'تحديث' })}
          >
            <RefreshCcw size={20} className={isLoadingAccounts ? 'animate-spin' : ''} />
          </button>
          <button
            type="button"
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${
              showAdvancedOptions
                ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30'
                : 'bg-white/5 border-white/20'
            } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={() => setShowAutoReconciliationModal(true)}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
          >
            <LinkIcon className="h-5 w-5" />
            <span>{p('auto_reconciliation')}</span>
          </button>
          <button
            type="button"
            onClick={() => setShowManualReconciliationModal(true)}
            className="bg-gradient-to-r from-green-500 to-green-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-green-800 transition-all duration-300 flex items-center space-x-2"
          >
            <LinkIcon className="h-5 w-5" />
            <span>{p('manual_reconciliation')}</span>
          </button>
        </>
      )}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: p('import', { defaultValue: t('common.import', { defaultValue: 'استيراد' }) }),
        exportLabel: p('export', { defaultValue: t('common.export', { defaultValue: 'تصدير' }) }),
        onImportCSV: () => { openUploadModal(null, 'pdf'); setShowImportDropdown(false); },
        onImportExcel: () => { openUploadModal(null, 'excel'); setShowImportDropdown(false); },
        onExportCSV: handleExportCSV,
        onExportExcel: handleExportExcel,
        onExportPDF: handleExportPDF,
      }}
    >

      {/* Advanced Options Section */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-6">
              <div className="space-y-6">
                <div>
                  <h3 className="text-white/80 text-sm font-medium mb-4 flex items-center gap-2"><Layers size={16} />{p('main_view_title')}</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { id: 'all_reconciliations', labelKey: 'tab_all_reconciliations', icon: LayoutGrid },
                      { id: 'by_bank', labelKey: 'tab_by_bank', icon: Building2 },
                      { id: 'by_status', labelKey: 'tab_by_status', icon: Shield }
                    ].map((tab) => (
                      <button key={tab.id} onClick={() => setMainViewMode(tab.id)} className={`px-4 py-2.5 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30' : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'}`}>
                        <tab.icon size={16} /><span className="text-sm">{p(tab.labelKey)}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white/80 text-sm font-medium flex items-center gap-2"><Filter size={16} />{p('quick_filters_title')}</h3>
                    {quickFilter !== 'all' && (<button onClick={() => { setQuickFilter('all'); setCurrentView('all'); }} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"><X size={12} />{p('reset_filter')}</button>)}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'all', labelKey: 'filter_all', count: stats.total },
                      { id: 'reconciled', labelKey: 'filter_reconciled', count: stats.reconciled },
                      { id: 'pending', labelKey: 'filter_pending', count: stats.pending },
                      { id: 'discrepancies', labelKey: 'filter_discrepancies', count: stats.discrepancies }
                    ].map((filter) => (
                      <button key={filter.id} onClick={() => { setQuickFilter(filter.id); setCurrentView(filter.id); }} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${quickFilter === filter.id ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10'}`}>
                        {p(filter.labelKey)} <span className="ml-1.5 text-xs opacity-70">({filter.count})</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-white/80 text-sm font-medium mb-4 flex items-center gap-2"><LayoutGrid size={16} />{p('distribution_title')}</h3>
                  <div {...getErpOverviewGridAttrs(3, '3')}>
                    {[
                      { labelKey: 'filter_reconciled', count: stats.reconciled, total: stats.total, color: 'bg-green-500', textColor: 'text-green-400', id: 'reconciled' },
                      { labelKey: 'filter_pending', count: stats.pending, total: stats.total, color: 'bg-amber-500', textColor: 'text-amber-400', id: 'pending' },
                      { labelKey: 'filter_discrepancies', count: stats.discrepancies, total: stats.total, color: 'bg-red-500', textColor: 'text-red-400', id: 'discrepancies' }
                    ].map((dist) => {
                      const percentage = dist.total > 0 ? Math.round((dist.count / dist.total) * 100) : 0;
                      return (
                        <button key={dist.id} onClick={() => setActiveDistributionFilter(activeDistributionFilter === dist.id ? null : dist.id)} className={`text-left p-4 rounded-xl transition-all ${activeDistributionFilter === dist.id ? 'bg-white/15 border-2 border-blue-400' : 'bg-white/5 border border-white/10 hover:bg-white/10'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white/80 text-sm font-medium">{p(dist.labelKey)}</span>
                            <span className={`${dist.textColor} text-sm font-bold`}>{dist.count}</span>
                          </div>
                          <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                            <div className={`${dist.color} h-full rounded-full transition-all duration-500`} style={{ width: `${percentage}%` }}></div>
                          </div>
                          <div className="mt-1 text-white/50 text-xs">{p('percent_of_total', { percent: percentage })}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Data Table with Integrated Header */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' ? (
          // Table View
          <div className="overflow-x-auto table-horizontal-scroll">
            {filteredAccounts.length === 0 ? (
              <div className="p-12 text-center text-white/60">
                <Building2 className="h-16 w-16 text-white/30 mb-4 mx-auto" />
                <h3 className="text-xl font-semibold text-white mb-2">{p('empty_title')}</h3>
                <p className="text-white/60 mb-6">{p('empty_message')}</p>
              </div>
            ) : (
              <table className="w-full clients-table data-table">
                <thead className="glass-table-head">
                  <tr>
                    <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                      <div className="flex justify-center">
                        <QawaemTableCheckbox
                          size="lg"
                          checked={allRowsSelected}
                          indeterminate={someRowsSelected}
                          onChange={handleSelectAll}
                        />
                      </div>
                    </th>
                    {isColVisible('account') ? (
                      <TableColumnHeaderFilter columnKey="account" label={p('col_account')} value={columnFilters.account || ''} onChange={(v) => setFilter('account', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColVisible('bank') ? (
                      <TableColumnHeaderFilter columnKey="bank" label={p('col_bank')} value={columnFilters.bank || ''} onChange={(v) => setFilter('bank', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColVisible('bankBalance') ? (
                      <TableColumnHeaderFilter columnKey="bankBalance" label={p('col_bank_balance')} value={columnFilters.bankBalance || ''} onChange={(v) => setFilter('bankBalance', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColVisible('accountingBalance') ? (
                      <TableColumnHeaderFilter columnKey="accountingBalance" label={p('col_accounting_balance')} value={columnFilters.accountingBalance || ''} onChange={(v) => setFilter('accountingBalance', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColVisible('reconciled') ? (
                      <TableColumnHeaderFilter columnKey="reconciled" label={p('col_reconciled')} value={columnFilters.reconciled || ''} onChange={(v) => setFilter('reconciled', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColVisible('pending') ? (
                      <TableColumnHeaderFilter columnKey="pending" label={p('col_pending')} value={columnFilters.pending || ''} onChange={(v) => setFilter('pending', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    {isColVisible('discrepancies') ? (
                      <TableColumnHeaderFilter columnKey="discrepancies" label={p('col_discrepancies')} value={columnFilters.discrepancies || ''} onChange={(v) => setFilter('discrepancies', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                    ) : null}
                    <QawaemTableHeaderActionsCell
                      label={p('col_actions')}
                      thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                      columns={qawaemColumnDefs}
                      onToggle={handleToggleColumn}
                      visibleCount={qawaemVisibleColumnCount}
                      totalCount={qawaemColumnDefs.length}
                    />
                  </tr>
                </thead>
                <tbody className="bg-white/5">
                  {filteredAccounts.map((account) => (
                    <tr key={account.id} className="hover:bg-white/10 transition-colors border-b border-white/10">
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedItems.includes(account.id)}
                            onChange={() => handleItemSelect(account.id)}
                          />
                        </div>
                      </td>
                      {isColVisible('account') ? (
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-blue-600/20 text-blue-400 rounded-lg flex items-center justify-center">
                              <Building2 className="h-6 w-6" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-white">{account.name}</div>
                              <div className="text-xs text-white/60">{account.accountNumber}</div>
                            </div>
                          </div>
                        </td>
                      ) : null}
                      {isColVisible('bank') ? (
                        <td className="px-6 py-4 text-white/80">{account.bank}</td>
                      ) : null}
                      {isColVisible('bankBalance') ? (
                        <td className="px-6 py-4 text-white font-medium">{formatMoney(account.bankBalance ?? account.balance)}</td>
                      ) : null}
                      {isColVisible('accountingBalance') ? (
                        <td className="px-6 py-4 text-white font-medium">{formatMoney(account.accountingBalance ?? account.balance)}</td>
                      ) : null}
                      {isColVisible('reconciled') ? (
                        <td className="px-6 py-4 text-green-400 font-semibold">{account.reconciled ?? 0}</td>
                      ) : null}
                      {isColVisible('pending') ? (
                        <td className="px-6 py-4 text-amber-400 font-semibold">{account.pending ?? 0}</td>
                      ) : null}
                      {isColVisible('discrepancies') ? (
                        <td className="px-6 py-4 text-red-400 font-semibold">{account.discrepancies ?? 0}</td>
                      ) : null}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex items-center justify-center gap-2">
                          {renderReconciliationStatusBadge(account)}
                          <QawaemErpRowActions
                            onView={() => handleView(account)}
                            viewTitle={p('action_view')}
                          />
                          <button
                            type="button"
                            onClick={() => setShowManualReconciliationModal(true)}
                            className="p-2 rounded-lg text-blue-400 hover:text-blue-300 hover:bg-white/10 transition-colors"
                            title={p('action_reconcile')}
                          >
                            <LinkIcon size={16} />
                          </button>
                          <Link
                            to={`/discrepancies?account=${account.id}`}
                            className="p-2 rounded-lg text-red-400 hover:text-red-300 hover:bg-white/10 transition-colors"
                            title={p('action_view_discrepancies')}
                          >
                            <AlertTriangle size={16} />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          // Card View
          <div className="p-6">
            {filteredAccounts.length === 0 ? (
              <div className="text-center text-white/60 py-12">
                <Building2 className="h-16 w-16 text-white/30 mb-4 mx-auto" />
                <h3 className="text-xl font-semibold text-white mb-2">{p('empty_title')}</h3>
                <p className="text-white/60">{p('empty_message')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredAccounts.map((account) => (
                  <motion.div
                    key={account.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 h-full min-w-0"
                  >
                    <div className="flex justify-between items-start gap-4 mb-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-semibold text-lg mb-1">{account.name}</h3>
                        <p className="text-white/60 text-sm">{account.bank} • {account.accountNumber}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {renderReconciliationStatusBadge(account)}
                        <QawaemErpRowActions
                          compact
                          size={14}
                          onView={() => handleView(account)}
                          viewTitle={p('action_view')}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-white/50 text-xs">{p('col_bank_balance')}</span>
                        <span className="text-white font-semibold">{formatMoney(account.bankBalance ?? account.balance)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-white/50 text-xs">{p('col_accounting_balance')}</span>
                        <span className="text-white font-semibold">{formatMoney(account.accountingBalance ?? account.balance)}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/10">
                        <div className="text-center">
                          <p className="text-green-400 font-bold text-lg">{account.reconciled}</p>
                          <p className="text-white/50 text-xs">{p('col_reconciled')}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-amber-400 font-bold text-lg">{account.pending}</p>
                          <p className="text-white/50 text-xs">{p('col_pending')}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-red-400 font-bold text-lg">{account.discrepancies}</p>
                          <p className="text-white/50 text-xs">{p('col_discrepancies')}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => setShowManualReconciliationModal(true)}
                        className="flex-1 bg-blue-600/20 text-blue-300 px-4 py-2 rounded-lg hover:bg-blue-600/30 transition-colors flex items-center justify-center space-x-1"
                      >
                        <LinkIcon className="h-4 w-4" />
                        <span className="text-sm">{p('action_reconcile')}</span>
                      </button>
                      <Link
                        to={`/discrepancies?account=${account.id}`}
                        className="bg-red-600/20 text-red-300 px-4 py-2 rounded-lg hover:bg-red-600/30 transition-colors flex items-center justify-center"
                        title={p('action_view_discrepancies')}
                      >
                        <AlertTriangle className="h-4 w-4" />
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Auto Reconciliation Modal — same shell as Nouvelle Écriture Comptable */}
      {showAutoReconciliationModal && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/20 backdrop-blur-md"
          onClick={() => setShowAutoReconciliationModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-[90%] max-w-7xl max-h-[90vh] flex flex-col overflow-hidden bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)' }}
          >
            <button
              type="button"
              onClick={() => setShowAutoReconciliationModal(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              aria-label={p('close_aria')}
            >
              <X className="h-6 w-6 text-white" />
            </button>
            <div className="overflow-y-auto flex-1 p-6">
              <AutoReconciliationPage />
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Manual Reconciliation Modal — same shell as Nouvelle Écriture Comptable */}
      {showManualReconciliationModal && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/20 backdrop-blur-md"
          onClick={() => setShowManualReconciliationModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-[90%] max-w-7xl max-h-[90vh] flex flex-col overflow-hidden bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)' }}
          >
            <button
              type="button"
              onClick={() => setShowManualReconciliationModal(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              aria-label={p('close_aria')}
            >
              <X className="h-6 w-6 text-white" />
            </button>
            <div className="overflow-y-auto flex-1 p-6">
              <ManualReconciliationPage />
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Upload Modal */}
      {showUploadModal && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/20 backdrop-blur-md"
          onClick={() => setShowUploadModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)' }}
          >
            <button
              type="button"
              onClick={() => setShowUploadModal(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              aria-label={p('close_aria')}
            >
              <X className="h-6 w-6 text-white" />
            </button>

            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{p('upload_modal_title')}</h2>
                <p className="text-white/60">
                  {uploadType === 'pdf' ? p('upload_format_pdf') : p('upload_format_excel')}
                </p>
              </div>

              {!uploadAccount && (
                <div className="bg-white/5 rounded-xl border border-white/20 p-4">
                  <label className="block w-full text-white font-medium mb-2 text-right">{p('upload_account_label')}</label>
                  <select
                    value={uploadAccount || ''}
                    onChange={(e) => setUploadAccount(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-400"
                  >
                    <option value="">{p('upload_select_account')}</option>
                    {accounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.name} ({account.bank})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* File Uploader */}
              <FileUploader
                accept={uploadType === 'pdf' ? ['.pdf'] : ['.xlsx', '.xls', '.csv']}
                maxSize={10 * 1024 * 1024}
                multiple={false}
                onUpload={handleFileUpload}
                disabled={isUploading || !uploadAccount}
              />

              {/* Info */}
              <div className="bg-blue-500/10 border border-blue-400/30 rounded-xl p-4">
                <h3 className="text-white font-medium mb-2">📊 {p('upload_extract_title')}</h3>
                <ul className="text-white/70 text-sm space-y-1">
                  <li>• {p('upload_extract_account')}</li>
                  <li>• {p('upload_extract_period')}</li>
                  <li>• {p('upload_extract_balances')}</li>
                  <li>• {p('upload_extract_transactions')}</li>
                  <li>• {p('upload_extract_validation')}</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Modal — guarded wrap (AGENTS §8) */}
      {showViewModal && selectedAccount ? (
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => setShowViewModal(false)}
            role="presentation"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '720px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Eye className="h-6 w-6 text-blue-400" />
                  {p('view_modal_title')}
                </h2>
                <button
                  type="button"
                  onClick={() => setShowViewModal(false)}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { label: p('col_account'), value: selectedAccount.name },
                  { label: p('col_bank'), value: selectedAccount.bank },
                  { label: p('col_bank_balance'), value: formatMoney(selectedAccount.bankBalance ?? selectedAccount.balance) },
                  { label: p('col_accounting_balance'), value: formatMoney(selectedAccount.accountingBalance ?? selectedAccount.balance) },
                  { label: p('col_reconciled'), value: selectedAccount.reconciled ?? 0 },
                  { label: p('col_pending'), value: selectedAccount.pending ?? 0 },
                  { label: p('col_discrepancies'), value: selectedAccount.discrepancies ?? 0 },
                ].map((row) => (
                  <div key={row.label}>
                    <p className="text-sm text-white/50 mb-1">{row.label}</p>
                    <p className="text-sm text-white font-medium">{row.value ?? '—'}</p>
                  </div>
                ))}
                <div>
                  <p className="text-sm text-white/50 mb-1">{p('col_status')}</p>
                  {renderReconciliationStatusBadge(selectedAccount, { clickable: false })}
                </div>
              </div>
            </motion.div>
          </div>,
          document.body
        )
      ) : null}
    </QawaemErpListPageShell>
  );
};

export default ReconciliationPage;


